import React, { useState, useEffect } from 'react';
import { Clock, Play, AlertCircle, RefreshCw, Users, Zap } from 'lucide-react';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const QueueMonitor = () => {
  const { t } = useLanguage();
  const [queueStatus, setQueueStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchQueueStatus = async () => {
    try {
      const response = await api.getQueueStatus();
      if (response.success) {
        setQueueStatus(response.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching queue status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueStatus();
    const interval = setInterval(fetchQueueStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  const getQueueColor = (queueType) => {
    switch (queueType) {
      case 'express':
        return 'text-red-400 bg-red-900/30 border-red-500/30';
      case 'priority':
        return 'text-orange-400 bg-orange-900/30 border-orange-500/30';
      case 'standard':
        return 'text-blue-400 bg-blue-900/30 border-blue-500/30';
      case 'economy':
        return 'text-green-400 bg-green-900/30 border-green-500/30';
      default:
        return 'text-gray-400 bg-gray-700 border-gray-600';
    }
  };

  const getQueueIcon = (queueType) => {
    switch (queueType) {
      case 'express':
        return <Zap size={16} />;
      case 'priority':
        return <Play size={16} />;
      case 'standard':
        return <Clock size={16} />;
      case 'economy':
        return <Users size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const formatEstimatedWait = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="animate-spin text-gray-400" size={24} />
          <span className="ml-2 text-gray-400">{t('loadingQueueStatus', 'Loading queue status...')}</span>
        </div>
      </div>
    );
  }

  if (!queueStatus) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center text-red-400">
          <AlertCircle size={20} className="mr-2" />
          <span>{t('unableToLoadQueueStatus', 'Unable to load queue status')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">{t('queueStatus', 'Queue Status')}</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchQueueStatus}
            className="p-2 text-gray-400 hover:text-white rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          {lastUpdate && (
            <span className="text-xs text-gray-400">
              {t('lastUpdated', 'Last updated')}: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-white">{t('systemStatus', 'System Status')}</h3>
            <p className="text-sm text-gray-400">
              {queueStatus.isPaused ? t('processingPaused', 'Processing paused') : t('processingActive', 'Processing active')}
            </p>
          </div>
          <div
            className={`flex items-center px-3 py-1 rounded-full text-sm ${
              queueStatus.isPaused ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                queueStatus.isPaused ? 'bg-red-400' : 'bg-green-400'
              }`}
            ></div>
            {queueStatus.isPaused ? t('paused', 'Paused') : t('active', 'Active')}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <h4 className="font-medium text-blue-400 mb-2">💡 {t('queueTips', 'Queue Tips')}</h4>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• {t('expressQueueTip', 'Express queue processes jobs fastest')}</li>
          <li>• {t('economyQueueTip', 'Economy queue is free but may have longer wait times')}</li>
          <li>• {t('queueOrderTip', 'Jobs are processed in priority order: Express → Priority → Standard → Economy')}</li>
          <li>• {t('processingTimeTip', 'Processing times depend on project complexity and current system load')}</li>
        </ul>
      </div>
    </div>
  );
};

export default QueueMonitor;
